import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Schedule,
  Send,
  CheckCircle,
  Warning,
  TrendingUp,
} from '@mui/icons-material';

interface PredictionResult {
  estimatedDays: number;
  confidence: number;
  factors: string[];
  recommendations: string[];
}

const DiagnosticDelay: React.FC = () => {
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    state: '',
    location: '',
    insurance: '',
    symptoms: '',
    duration: '',
    specialists: '',
  });

  const [prediction, setPrediction] = useState<PredictionResult | null>(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const calculatePrediction = (): PredictionResult => {
    const age = parseInt(formData.age);
    const duration = parseInt(formData.duration);
    const specialists = parseInt(formData.specialists);
    
    // Base delay (national average)
    let estimatedDays = 365;
    const factors: string[] = [];
    const recommendations: string[] = [];
    
    // Gender factor
    if (formData.gender === 'Female') {
      const genderDelay = 120;
      estimatedDays += genderDelay;
      factors.push(`Female gender (+${genderDelay} days)`);
      recommendations.push('Advocate for thorough evaluation - women often face diagnostic bias');
    } else if (formData.gender === 'Male') {
      const genderDelay = -30;
      estimatedDays += genderDelay;
      factors.push(`Male gender (${genderDelay} days)`);
    }
    
    // Age factor
    if (age > 50) {
      const ageDelay = 45;
      estimatedDays += ageDelay;
      factors.push(`Age over 50 (+${ageDelay} days)`);
      recommendations.push('Request age-appropriate screening tests');
    } else if (age < 30) {
      const ageDelay = 60;
      estimatedDays += ageDelay;
      factors.push(`Young age - symptoms often dismissed (+${ageDelay} days)`);
      recommendations.push('Be persistent - young patients often face dismissal');
    }
    
    // Location factor
    if (formData.location === 'Rural') {
      const locationDelay = 85;
      estimatedDays += locationDelay;
      factors.push(`Rural location (+${locationDelay} days)`);
      recommendations.push('Consider telehealth consultations with specialists');
      recommendations.push('Explore travel options to urban medical centers');
    } else if (formData.location === 'Suburban') {
      const locationDelay = 30;
      estimatedDays += locationDelay;
      factors.push(`Suburban location (+${locationDelay} days)`);
    } else {
      const locationDelay = -20;
      estimatedDays += locationDelay;
      factors.push(`Urban location (${locationDelay} days)`);
    }
    
    // Insurance factor
    if (formData.insurance === 'Uninsured') {
      const insuranceDelay = 150;
      estimatedDays += insuranceDelay;
      factors.push(`Uninsured status (+${insuranceDelay} days)`);
      recommendations.push('Contact patient advocacy groups for financial assistance');
      recommendations.push('Explore community health centers and sliding scale clinics');
    } else if (formData.insurance === 'Medicaid') {
      const insuranceDelay = 75;
      estimatedDays += insuranceDelay;
      factors.push(`Medicaid coverage (+${insuranceDelay} days)`);
      recommendations.push('Verify specialist acceptance of Medicaid before appointments');
    } else if (formData.insurance === 'Medicare') {
      const insuranceDelay = 40;
      estimatedDays += insuranceDelay;
      factors.push(`Medicare coverage (+${insuranceDelay} days)`);
    } else {
      const insuranceDelay = -25;
      estimatedDays += insuranceDelay;
      factors.push(`Private insurance (${insuranceDelay} days)`);
    }
    
    // Symptom duration factor
    if (duration > 12) {
      const durationDelay = 90;
      estimatedDays += durationDelay;
      factors.push(`Long symptom duration (+${durationDelay} days)`);
      recommendations.push('Emphasize symptom chronicity to providers');
    } else if (duration > 6) {
      const durationDelay = 45;
      estimatedDays += durationDelay;
      factors.push(`Moderate symptom duration (+${durationDelay} days)`);
    }
    
    // Specialists seen factor
    if (specialists === 0) {
      const specialistDelay = 120;
      estimatedDays += specialistDelay;
      factors.push(`No specialists consulted yet (+${specialistDelay} days)`);
      recommendations.push('Request referral to specialist immediately');
    } else if (specialists >= 3) {
      const specialistDelay = 60;
      estimatedDays += specialistDelay;
      factors.push(`Multiple specialists seen - complex case (+${specialistDelay} days)`);
      recommendations.push('Consider seeking second opinion at academic medical center');
      recommendations.push('Request case review by multidisciplinary team');
    } else {
      const specialistDelay = 30;
      estimatedDays += specialistDelay;
      factors.push(`Specialist consultation in progress (+${specialistDelay} days)`);
    }
    
    // Symptom complexity (based on description length)
    const symptomWords = formData.symptoms.trim().split(/\s+/).length;
    if (symptomWords > 20) {
      const complexityDelay = 50;
      estimatedDays += complexityDelay;
      factors.push(`Complex symptom presentation (+${complexityDelay} days)`);
      recommendations.push('Create detailed symptom timeline with triggers and patterns');
    }
    
    // State-specific adjustments (sample states)
    const stateDelays: { [key: string]: number } = {
      'CA': -30, 'NY': -20, 'MA': -25,
      'TX': 40, 'FL': 30, 'OH': 50,
      'WA': -15, 'IL': 20, 'PA': 35,
    };
    
    const stateCode = formData.state.toUpperCase();
    if (stateDelays[stateCode]) {
      const stateDelay = stateDelays[stateCode];
      estimatedDays += stateDelay;
      const sign = stateDelay > 0 ? '+' : '';
      factors.push(`${stateCode} state healthcare access (${sign}${stateDelay} days)`);
    }
    
    // Add general recommendations
    recommendations.push('Document all symptoms in detailed diary with dates and severity');
    recommendations.push('Bring comprehensive medical history to all appointments');
    recommendations.push('Join patient advocacy groups for support and resources');
    
    // Calculate confidence based on data completeness
    let confidence = 70;
    if (age > 0) confidence += 5;
    if (duration > 0) confidence += 5;
    if (specialists >= 0) confidence += 5;
    if (formData.symptoms.length > 20) confidence += 5;
    if (stateDelays[stateCode]) confidence += 5;
    
    // Ensure minimum delay
    estimatedDays = Math.max(estimatedDays, 90);
    
    return {
      estimatedDays: Math.round(estimatedDays),
      confidence: Math.min(confidence, 95),
      factors,
      recommendations: [...new Set(recommendations)], // Remove duplicates
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API processing time
    setTimeout(() => {
      const calculatedPrediction = calculatePrediction();
      setPrediction(calculatedPrediction);
      setLoading(false);
    }, 1500);
  };

  const getSeverityColor = (days: number) => {
    if (days < 180) return 'success';
    if (days < 365) return 'warning';
    return 'error';
  };

  const getSeverityLabel = (days: number) => {
    if (days < 180) return 'Low Delay';
    if (days < 365) return 'Moderate Delay';
    return 'High Delay';
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          <Schedule sx={{ mr: 2, verticalAlign: 'middle' }} />
          Diagnostic Delay Predictor
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          AI-powered prediction of time-to-diagnosis based on patient demographics and symptoms
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Input Form */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Patient Information
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      label="Gender"
                    >
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Non-binary">Non-binary</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="e.g., CA, NY, TX"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Location Type</InputLabel>
                    <Select
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      label="Location Type"
                    >
                      <MenuItem value="Urban">Urban</MenuItem>
                      <MenuItem value="Suburban">Suburban</MenuItem>
                      <MenuItem value="Rural">Rural</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Insurance Type</InputLabel>
                    <Select
                      name="insurance"
                      value={formData.insurance}
                      onChange={handleInputChange}
                      label="Insurance Type"
                    >
                      <MenuItem value="Private">Private</MenuItem>
                      <MenuItem value="Medicare">Medicare</MenuItem>
                      <MenuItem value="Medicaid">Medicaid</MenuItem>
                      <MenuItem value="Uninsured">Uninsured</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Primary Symptoms"
                    name="symptoms"
                    value={formData.symptoms}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    placeholder="Describe your main symptoms"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Symptom Duration (months)"
                    name="duration"
                    type="number"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Specialists Seen"
                    name="specialists"
                    type="number"
                    value={formData.specialists}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Send />}
                    disabled={loading}
                  >
                    {loading ? 'Predicting...' : 'Predict Delay'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Results */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, minHeight: '600px' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Prediction Results
            </Typography>
            {!prediction && !loading && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Schedule sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                  Enter patient information to predict diagnostic delay
                </Typography>
              </Box>
            )}
            {loading && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  Analyzing data with ML model...
                </Typography>
              </Box>
            )}
            {prediction && !loading && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={getSeverityColor(prediction.estimatedDays)} sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Estimated Diagnostic Delay
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ my: 1 }}>
                    {prediction.estimatedDays} days
                  </Typography>
                  <Typography variant="body2">
                    Approximately {Math.round(prediction.estimatedDays / 30)} months | {getSeverityLabel(prediction.estimatedDays)}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Confidence: {prediction.confidence}%
                  </Typography>
                </Alert>

                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Contributing Factors
                    </Typography>
                    <List dense>
                      {prediction.factors.map((factor, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Warning color="warning" />
                          </ListItemIcon>
                          <ListItemText primary={factor} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>

                <Divider sx={{ my: 2 }} />

                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      <CheckCircle sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
                      Recommended Actions
                    </Typography>
                    <List dense>
                      {prediction.recommendations.map((rec, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircle color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>

                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Note:</strong> This prediction is based on historical data and ML models. 
                    Actual diagnostic timelines may vary. Consult with healthcare providers for personalized guidance.
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Statistics Section */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={3}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                National Average
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold">
                547 days
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                18 months to diagnosis
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={3}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Gender Gap
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold" color="error">
                +72%
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Women wait longer
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={3}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Rural Penalty
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                +45%
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Longer in rural areas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DiagnosticDelay;

// Made with Bob
